import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Camera, DollarSign, Send, Ruler, CheckCircle2, ArrowRight, Zap } from "lucide-react";

export default function Landing() {
  const nav = useNavigate();
  const heroImg = "https://images.pexels.com/photos/4876639/pexels-photo-4876639.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
  const featureImg1 = "https://images.pexels.com/photos/8985720/pexels-photo-8985720.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
  const featureImg2 = "https://images.pexels.com/photos/27309420/pexels-photo-27309420.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-sm">
              <Zap className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="font-display text-2xl font-black tracking-tight">QUOTEBOT</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="hover:text-primary transition-colors">Features</a>
            <a href="#pricing" className="hover:text-primary transition-colors">Pricing</a>
            <a href="#how" className="hover:text-primary transition-colors">How it works</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button data-testid="nav-login-btn" variant="ghost" onClick={() => nav("/login")}>Log in</Button>
            <Button data-testid="nav-signup-btn" className="rounded-sm btn-kinetic" onClick={() => nav("/register")}>
              Start free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 border border-border rounded-sm px-3 py-1 text-xs uppercase tracking-widest text-primary mb-6">
              <Zap className="w-3.5 h-3.5" /> AI-powered field estimator
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] mb-6">
              Snap a photo.<br />
              <span className="hl-orange">Send a quote.</span><br />
              Get paid faster.
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
              QuoteBot analyzes any driveway, roof, lawn, or vehicle from a single photo and instantly generates a priced quote, invoice, and payment link — before you leave the jobsite.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button data-testid="hero-start-btn" size="lg" className="h-14 px-8 rounded-sm text-base btn-kinetic" onClick={() => nav("/register")}>
                Start free — no card required <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button data-testid="hero-see-pricing" size="lg" variant="outline" className="h-14 px-8 rounded-sm text-base" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}>
                See pricing
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> 5 niches supported</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Stripe payouts</div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-3 bg-primary/30 blur-3xl rounded-full" />
            <div className="relative border border-border rounded-sm overflow-hidden bento-card">
              <img src={heroImg} alt="Contractor on site" className="w-full h-[420px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-slate-950/85 backdrop-blur border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-widest text-primary">AI ESTIMATE · GENERATED</span>
                  <span className="text-xs text-muted-foreground">14s</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><p className="text-muted-foreground text-xs">Driveway</p><p className="font-mono font-semibold">640 sqft</p></div>
                  <div><p className="text-muted-foreground text-xs">Walkway</p><p className="font-mono font-semibold">180 sqft</p></div>
                  <div><p className="text-muted-foreground text-xs">Quote</p><p className="font-mono font-semibold text-primary">$254.00</p></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-widest text-primary mb-3">Built for the field</p>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight mb-4">Every step from photo to payout.</h2>
            <p className="text-muted-foreground text-lg">One tool replaces the tape measure, the notebook, the quote template, the invoice PDF, and the follow-up spreadsheet.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Camera, title: "Snap or upload", desc: "Take a phone photo of any property. Works with driveways, roofs, lawns, fences, vehicles." },
              { icon: Ruler, title: "AI measures", desc: "Claude Sonnet 4.6 vision computes square footage, linear footage, and surface condition." },
              { icon: DollarSign, title: "Priced quote", desc: "Your rate card is applied. Editable line items. Tax and totals recomputed live." },
              { icon: Send, title: "Auto follow-up", desc: "Email & SMS reminders scheduled automatically. Convert 40% more quotes." },
            ].map((f) => (
              <div key={f.title} className="bento-card p-6">
                <div className="w-11 h-11 bg-primary/10 border border-primary/20 flex items-center justify-center rounded-sm mb-4">
                  <f.icon className="w-5 h-5 text-primary" strokeWidth={2} />
                </div>
                <h3 className="font-display text-2xl font-bold mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="bento-card overflow-hidden group relative">
              <img src={featureImg1} alt="Mobile estimator" className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="p-6">
                <h3 className="font-display text-3xl font-bold mb-2">Works where you work</h3>
                <p className="text-sm text-muted-foreground">Sun-legible interface with big tap targets. Optimized for one-handed use in gloves.</p>
              </div>
            </div>
            <div className="bento-card overflow-hidden group relative">
              <img src={featureImg2} alt="Landscaping" className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="p-6">
                <h3 className="font-display text-3xl font-bold mb-2">Five verticals, one workflow</h3>
                <p className="text-sm text-muted-foreground">Pressure washing · Landscaping · Painting · Roofing · Mobile detailing.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How */}
      <section id="how" className="border-t border-border bg-slate-950/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
          <div className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-widest text-primary mb-3">Workflow</p>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight">From driveway to deposit in under 3 minutes.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Capture", d: "Point your camera at the job. One frame is enough — the AI handles the rest." },
              { n: "02", t: "Confirm", d: "Review measurements and line items. Tweak the quote in seconds if needed." },
              { n: "03", t: "Collect", d: "Send by SMS or email with a Stripe pay-link. Get notified when the customer taps it." },
            ].map((s) => (
              <div key={s.n} className="border border-border rounded-sm p-6 bg-card">
                <p className="font-display text-primary text-6xl font-black leading-none mb-4">{s.n}</p>
                <h3 className="font-display text-2xl font-bold mb-2">{s.t}</h3>
                <p className="text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-primary mb-3">Pricing</p>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight">Priced like a job well done.</h2>
            <p className="text-muted-foreground mt-3">Flat monthly plans. Cancel anytime. Stripe processing fees apply on payments.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: "Solo", price: 49, best: false, feats: ["25 AI estimates/mo", "1 user", "Email follow-ups", "Stripe payment links"] },
              { name: "Crew", price: 99, best: true, feats: ["150 AI estimates/mo", "5 users", "Email + SMS follow-ups", "Custom branding", "Priority support"] },
              { name: "Business", price: 149, best: false, feats: ["Unlimited estimates", "Unlimited users", "Advanced AI (paint correction, roof pitch)", "API access", "Dedicated success manager"] },
            ].map((p) => (
              <div key={p.name} className={`border rounded-sm p-6 relative ${p.best ? "border-primary bg-card" : "border-border bg-card"}`}>
                {p.best && <span data-testid={`plan-best-${p.name}`} className="absolute -top-3 left-6 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm">Most popular</span>}
                <p className="font-display text-2xl font-bold">{p.name}</p>
                <div className="mt-3 mb-4">
                  <span className="font-display text-5xl font-black">${p.price}</span>
                  <span className="text-muted-foreground text-sm">/month</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {p.feats.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  className={`w-full h-12 rounded-sm btn-kinetic ${p.best ? "" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}
                  onClick={() => nav("/register")}
                >
                  Start {p.name}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary flex items-center justify-center rounded-sm">
              <Zap className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold">QUOTEBOT</span>
          </div>
          <p className="text-xs text-muted-foreground">© 2026 QuoteBot Estimator · Built for the trades.</p>
        </div>
      </footer>
    </div>
  );
}
